import { RxDocument } from 'rxdb';
import { FieldTemplateItem } from '@/typings/questionnaires-api-models';
import { ApiDefinition } from '@/tsClient/servers/noodleQuestionnaires';
import { cloneDeep } from 'lodash';
import { getNoodleDb, purgeCollectionsData } from './rxdb.noodle';
import { MAX_CACHE_TIME, REPLICATION_INFO_ID, LAST_REPLICATED_DATE_KEY } from './rxdb.constants';

// in memory cache for the last replicated date
let lastReplicatedDate: number | null = null;

// in memory cache for the check if data is stale promise
let checkIfDataIsStalePromise: Promise<boolean> | null = null;

const joinValues = (...args: (string | null | undefined)[]): string => args.filter(Boolean).join('-');

function getJsonMutable<T>(items: RxDocument<T>[]): T[] {
  return items.map(item => item.toMutableJSON());
}

/**
 * Check Data Stale Status
 *
 * This function checks if the data is stale based on the last replicated date.
 * It returns true if the data is stale or if the last replicated date is not set, false otherwise.
 *
 * If the lastReplicatedDate variable is already set, it will use it as the last replicated date.
 * Otherwise, it will read the last replicated date from the database.
 *
 * The lastReplicatedDate variable is reset when the data is stale and the collections are purged.
 */
async function isDataStale(): Promise<{ errored?: boolean; isFirstTime?: boolean; isStale: boolean }> {
  const checkStaleTime = (time: number): boolean => Date.now() - time > MAX_CACHE_TIME;

  try {
    if (lastReplicatedDate) return { isStale: checkStaleTime(lastReplicatedDate) };

    const db = await getNoodleDb();
    const replicationInfoDoc = await db.getLocal(REPLICATION_INFO_ID);
    if (!replicationInfoDoc) return { isFirstTime: true, isStale: true };

    lastReplicatedDate = replicationInfoDoc.get(LAST_REPLICATED_DATE_KEY) || 0;
    return { isStale: checkStaleTime(lastReplicatedDate!) };
  } catch (error) {
    console.error('RXDB API - An error occurred while checking the stale status for the replication info', error);
    return { errored: true, isStale: false };
  }
}

/**
 * Update Last Replicated Date
 *
 * This function updates the last replicated date for the replication info.
 * If the lastReplicatedDate variable is already set, the function will not update it.
 *
 * The lastReplicatedDate variable is reset when the data is stale and the collections are purged.
 */
async function updateLastReplicatedDate(): Promise<void> {
  try {
    if (lastReplicatedDate) return;

    const db = await getNoodleDb();
    const now = Date.now();

    lastReplicatedDate = now;
    await db.upsertLocal(REPLICATION_INFO_ID, { [LAST_REPLICATED_DATE_KEY]: now });
  } catch (error) {
    console.error('RXDB API - An error occurred while updating the last replicated date', error);
  }
}

/**
 * Check If Data Is Stale
 *
 * This function checks if the data is stale and purges the collections if it is.
 * It returns true if the data is stale, false otherwise.
 *
 * The execution of this function is cached in the checkIfDataIsStalePromise variable to avoid executing it multiple times as
 * purging the collections is an expensive operation.
 */
async function checkIfDataIsStale(): Promise<boolean> {
  if (checkIfDataIsStalePromise) return checkIfDataIsStalePromise;

  const verifying = async (): Promise<boolean> => {
    try {
      const { isStale, isFirstTime } = await isDataStale();

      if (isFirstTime) {
        // If the data is first time being checked, update the last replicated date
        await updateLastReplicatedDate();
        return true;
      }

      if (!isStale) return false;

      // Purge the collections
      await purgeCollectionsData();

      // Reset the last replicated date to null
      // This is important as other functions will check if this variable is already set and
      // will ignore reading/updating it from the database
      lastReplicatedDate = null;

      // Update the last replicated date
      await updateLastReplicatedDate();

      return true;
    } catch (error) {
      console.error('RXDB API - An error occurred while checking if the data is stale', error);
      return true;
    }
  };

  checkIfDataIsStalePromise = verifying();
  const isStale = await checkIfDataIsStalePromise;
  checkIfDataIsStalePromise = null;

  return isStale;
}

/* ================================
 * Field Templates for Section Template
 * ================================
 */

type FieldTemplatesForSectionTemplateApi =
  ApiDefinition['/questionnaire-templates/{creatorId}/{questionnaireTemplateId}/section-templates/{sectionTemplateId}/field-templates']['GET'];
type FieldTemplatesForSectionTemplateInput = FieldTemplatesForSectionTemplateApi['params'];

async function getFieldTemplatesForSectionTemplate({
  questionnaireTemplateId,
  sectionTemplateId,
}: FieldTemplatesForSectionTemplateInput): Promise<FieldTemplateItem[] | null> {
  try {
    const db = await getNoodleDb();
    const docs = await db.qreFieldTemplate.find({ selector: { questionnaireTemplateId, sectionTemplateId }, sort: [{ rank: 'asc' }] }).exec();

    if (!docs.length) return null;

    return getJsonMutable(docs) as FieldTemplateItem[];
  } catch (error) {
    console.error('RXDB API - An error occurred while getting the field templates from RxDB', error);
    return null;
  }
}

async function insertFieldTemplatesForSectionTemplate(fieldTemplates: FieldTemplateItem[]): Promise<boolean> {
  try {
    const db = await getNoodleDb();
    const clonedFieldTemplates = cloneDeep(fieldTemplates);
    const withCompoundIdFieldTemplates = clonedFieldTemplates.map(fieldTemplate => ({
      ...fieldTemplate,
      compoundId: joinValues(fieldTemplate.id, fieldTemplate.sectionTemplateId, fieldTemplate.listFieldTemplateId),
    }));

    const result = await db.qreFieldTemplate.bulkUpsert(withCompoundIdFieldTemplates);

    if (result.error.length) {
      console.error('RXDB API - Errors found while inserting the field templates into RxDB', result.error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('RXDB API - An error occurred while inserting the field templates into RxDB', error);
    return false;
  }
}

export async function rxGetFieldTemplatesForSectionTemplateHandler(
  input: FieldTemplatesForSectionTemplateInput,
  fetchFromApi: () => Promise<FieldTemplateItem[]>,
): Promise<FieldTemplateItem[]> {
  const fetchAndSave = async (): Promise<FieldTemplateItem[]> => {
    const fieldTemplatesFromApi = await fetchFromApi();
    await insertFieldTemplatesForSectionTemplate(fieldTemplatesFromApi);
    return fieldTemplatesFromApi;
  };

  const isStale = await checkIfDataIsStale();
  if (isStale) return fetchAndSave();

  const cachedFieldTemplates = await getFieldTemplatesForSectionTemplate(input);
  if (cachedFieldTemplates) return cachedFieldTemplates;

  return fetchAndSave();
}

/* ================================
 * Questionnaire Template Of Questionnaire
 * ================================
 */

type QuestionnaireTemplateOfQuestionnaireApi = ApiDefinition['/questionnaires/{questionnaireId}/questionnaire-template']['GET'];
type QuestionnaireTemplateOfQuestionnaireInput = QuestionnaireTemplateOfQuestionnaireApi['params'];
type QuestionnaireTemplate = QuestionnaireTemplateOfQuestionnaireApi['response'];

async function getQuestionnaireTemplateOfQuestionnaire({
  questionnaireId,
}: QuestionnaireTemplateOfQuestionnaireInput): Promise<QuestionnaireTemplate | null> {
  try {
    const db = await getNoodleDb();
    const docsMap = await db.qreTemplateOfQre.findByIds([questionnaireId]).exec();

    const rxItem = docsMap.get(questionnaireId);
    if (!rxItem) return null;

    return getJsonMutable([rxItem])[0] as QuestionnaireTemplate;
  } catch (error) {
    console.error('RXDB API - An error occurred while getting the questionnaire template of questionnaire', error);
    return null;
  }
}

async function insertQuestionnaireTemplateOfQuestionnaire(
  { questionnaireId }: QuestionnaireTemplateOfQuestionnaireInput,
  questionnaireTemplate: QuestionnaireTemplate,
): Promise<boolean> {
  try {
    const db = await getNoodleDb();
    const clonedQuestionnaireTemplate = cloneDeep(questionnaireTemplate);

    await db.qreTemplateOfQre.upsert({
      ...clonedQuestionnaireTemplate,
      questionnaireId,
    });

    return true;
  } catch (error) {
    console.error('RXDB API - An error occurred while inserting the item into RxDB', error);
    return false;
  }
}

export async function rxGetQuestionnaireTemplateOfQuestionnaireHandler(
  input: QuestionnaireTemplateOfQuestionnaireInput,
  fetchFromApi: () => Promise<QuestionnaireTemplate>,
): Promise<QuestionnaireTemplate> {
  const fetchAndSave = async (): Promise<QuestionnaireTemplate> => {
    const questionnaireTemplateFromApi = await fetchFromApi();
    await insertQuestionnaireTemplateOfQuestionnaire(input, questionnaireTemplateFromApi);
    return questionnaireTemplateFromApi;
  };

  const isStale = await checkIfDataIsStale();
  if (isStale) return fetchAndSave();

  const cachedQuestionnaireTemplate = await getQuestionnaireTemplateOfQuestionnaire(input);
  if (cachedQuestionnaireTemplate) return cachedQuestionnaireTemplate;

  return fetchAndSave();
}
